import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaGlobe,
  FaBrain,
  FaEye,
  FaNetworkWired,
  FaRobot,
  FaCloud,
  FaArrowRight,
  FaChevronRight,
} from "react-icons/fa";
import Itservices from "../inc/Itservice";
import Services3  from "../images/technology.jpg";
import WhatsApp   from "../inc/Whatsapp";

/* ─────────────────────────────────────────────────────────────
   Data
───────────────────────────────────────────────────────────── */
const TECH_ITEMS = [
  {
    id:           "web",
    icon:         <FaGlobe />,
    title:        "Web Technology",
    tag:          "Frontend & Backend",
    desc:         "Core of online presence. HTML, CSS, and JavaScript create dynamic, scalable websites that ensure user engagement and smooth functionality across all devices.",
    accent:       "#2563eb",
    accentBg:     "rgba(37,99,235,0.08)",
    accentBorder: "rgba(37,99,235,0.2)",
    stack:        ["React", "Node.js", "REST APIs", "TypeScript"],
  },
  {
    id:           "ai",
    icon:         <FaBrain />,
    title:        "AI & Machine Learning",
    tag:          "Intelligent Systems",
    desc:         "Powerful AI algorithms, neural networks, and ML models that solve complex problems and unlock data-driven insights to automate decisions at scale.",
    accent:       "#7c3aed",
    accentBg:     "rgba(124,58,237,0.08)",
    accentBorder: "rgba(124,58,237,0.2)",
    stack:        ["Python", "TensorFlow", "PyTorch", "LLMs"],
  },
  {
    id:           "cv",
    icon:         <FaEye />,
    title:        "Computer Vision",
    tag:          "Visual Intelligence",
    desc:         "From image processing to real-time object detection and pattern recognition — turning visual information into actionable, automated intelligence.",
    accent:       "#0891b2",
    accentBg:     "rgba(8,145,178,0.08)",
    accentBorder: "rgba(8,145,178,0.2)",
    stack:        ["OpenCV", "YOLO", "CNN", "Edge AI"],
  },
  {
    id:           "iot",
    icon:         <FaNetworkWired />,
    title:        "IoT",
    tag:          "Connected Devices",
    desc:         "Interconnected devices and sensors enabling smarter operations, predictive analytics, and seamless real-time monitoring across industrial environments.",
    accent:       "#059669",
    accentBg:     "rgba(5,150,105,0.08)",
    accentBorder: "rgba(5,150,105,0.2)",
    stack:        ["MQTT", "Edge Computing", "Sensors", "Embedded"],
  },
  {
    id:           "rpa",
    icon:         <FaRobot />,
    title:        "RPA & Automation",
    tag:          "Process Automation",
    desc:         "Intelligent automation with robotic process automation and bots to eliminate repetitive tasks, reduce errors, and boost operational productivity.",
    accent:       "#dc2626",
    accentBg:     "rgba(220,38,38,0.08)",
    accentBorder: "rgba(220,38,38,0.2)",
    stack:        ["Bot Framework", "Workflows", "APIs"],
  },
  {
    id:           "cloud",
    icon:         <FaCloud />,
    title:        "Cloud Computing",
    tag:          "Scalable Infrastructure",
    desc:         "On-demand resources, scalable infrastructure, and SaaS/PaaS/IaaS solutions delivering flexibility, performance, and reliability on the cloud.",
    accent:       "#ea580c",
    accentBg:     "rgba(234,88,12,0.08)",
    accentBorder: "rgba(234,88,12,0.2)",
    stack:        ["AWS", "Azure", "Docker", "Kubernetes"],
  },
];

const STATS = [
  { id: "s1", value: "6+",  label: "Technology Domains"  },
  { id: "s2", value: "10+", label: "Projects Delivered"  },
  { id: "s3", value: "4+", label: "Enterprise Clients"  },
  { id: "s4", value: "99%", label: "Client Satisfaction" },
];

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
function Technology() {
  const [activeId, setActiveId] = useState(null);

  const toggle = (id) => setActiveId((prev) => (prev === id ? null : id));

  return (
    <>
      <style>{`
        /*
         * System font stack — no external download.
         *   macOS / iOS  → San Francisco (-apple-system)
         *   Windows      → Segoe UI
         *   Android      → Roboto
         *   Fallback     → Helvetica → Arial → sans-serif
         *
         * Both --font-display (headings) and --font-body (body)
         * use the same stack for a clean, unified, professional look.
         */

        :root {
          --tc-bg:           #f0f4f9;
          --tc-card:         #ffffff;
          --tc-text-dark:    #0f172a;
          --tc-text-body:    #475569;
          --tc-text-muted:   #94a3b8;
          --tc-border:       #e2e8f0;
          --tc-accent:       #2563eb;
          --tc-shadow:       0 4px 24px rgba(0,0,0,0.07);
          --tc-shadow-hover: 0 12px 36px rgba(0,0,0,0.13);
          --tc-radius:       16px;

          --font-display: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          --font-body:    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        }

        .tc-page {
          background: var(--tc-bg);
          font-family: var(--font-body);
          min-height: 100vh;
        }

        /* ── Hero ── */
        .tc-hero {
          position: relative;
          min-height: 340px;
          background-size: cover;
          background-position: center;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tc-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(10,20,50,0.88) 0%, rgba(15,50,120,0.72) 100%);
        }
        .tc-hero-content {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 4rem 1.5rem;
        }
        .tc-hero-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          color: rgba(255,255,255,0.88);
          font-family: var(--font-body);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 0.32rem 1rem;
          border-radius: 50px;
          margin-bottom: 1.1rem;
        }
        .tc-hero-title {
          font-family: var(--font-display);
          font-size: clamp(2rem, 5vw, 3.2rem);
          font-weight: 600;
          color: #fff;
          margin: 0 0 0.75rem;
          line-height: 1.3;
        }
        .tc-hero-title span {
          background: linear-gradient(90deg, #60a5fa, #a78bfa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .tc-hero-sub {
          font-family: var(--font-body);
          font-size: 1.05rem;
          font-weight: 400;
          color: rgba(255,255,255,0.7);
          max-width: 520px;
          margin: 0 auto;
          line-height: 1.75;
        }

        /* ── Container ── */
        .tc-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem 5rem;
        }

        /* ── Floating card ── */
        .tc-card-wrap {
          background: var(--tc-card);
          border-radius: var(--tc-radius);
          box-shadow: var(--tc-shadow);
          margin-top: -52px;
          position: relative;
          z-index: 2;
          overflow: hidden;
        }

        /* ── Breadcrumb ── */
        .tc-breadcrumb {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.9rem 2rem;
          border-bottom: 1px solid var(--tc-border);
          font-family: var(--font-body);
          font-size: 0.82rem;
          color: var(--tc-text-muted);
        }
        .tc-breadcrumb a {
          color: var(--tc-accent);
          text-decoration: none;
          font-weight: 500;
        }
        .tc-breadcrumb a:hover { text-decoration: underline; }
        .tc-breadcrumb svg { font-size: 0.55rem; opacity: 0.5; }

        /* ── Card body ── */
        .tc-card-body { padding: 2.5rem 2rem 2rem; }

        .tc-section-eyebrow {
          font-family: var(--font-body);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--tc-accent);
          margin-bottom: 0.4rem;
        }
        .tc-section-title {
          font-family: var(--font-display);
          font-size: clamp(1.5rem, 3vw, 2rem);
          font-weight: 600;
          color: var(--tc-text-dark);
          margin: 0 0 0.4rem;
          line-height: 1.3;
        }
        .tc-underline {
          width: 48px; height: 3px;
          background: var(--tc-accent);
          border-radius: 2px;
          margin-bottom: 1rem;
        }
        .tc-section-desc {
          font-family: var(--font-body);
          font-size: 1rem;
          font-weight: 400;
          color: var(--tc-text-body);
          line-height: 1.75;
          max-width: 680px;
          margin-bottom: 2.5rem;
        }
        .tc-section-desc strong {
          color: var(--tc-text-dark);
          font-weight: 600;
        }

        /* ── Tech grid ── */
        .tc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.25rem;
        }

        /* ── Tech card ── */
        .tc-tech-card {
          position: relative;
          border-radius: 12px;
          border: 1.5px solid var(--tc-border);
          background: var(--tc-card);
          padding: 1.5rem;
          cursor: pointer;
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
          overflow: hidden;
        }
        .tc-tech-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: var(--card-accent, #2563eb);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.3s ease;
        }
        .tc-tech-card:hover,
        .tc-tech-card.active {
          transform: translateY(-5px);
          box-shadow: var(--tc-shadow-hover);
          border-color: var(--card-accent, #2563eb);
        }
        .tc-tech-card:hover::before,
        .tc-tech-card.active::before { transform: scaleX(1); }

        /* Card icon */
        .tc-card-icon {
          width: 44px; height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          margin-bottom: 1rem;
          background: var(--card-accent-bg, rgba(37,99,235,0.08));
          border: 1px solid var(--card-accent-border, rgba(37,99,235,0.2));
          color: var(--card-accent, #2563eb);
          transition: transform 0.2s ease;
        }
        .tc-tech-card:hover .tc-card-icon { transform: scale(1.1) rotate(-4deg); }

        .tc-card-tag {
          font-family: var(--font-body);
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--card-accent, #2563eb);
          margin-bottom: 0.35rem;
          opacity: 0.85;
        }
        .tc-card-title {
          font-family: var(--font-display);
          font-size: 1.05rem;
          font-weight: 600;
          color: var(--tc-text-dark);
          margin-bottom: 0.6rem;
          line-height: 1.3;
        }
        .tc-card-desc {
          font-family: var(--font-body);
          font-size: 0.87rem;
          font-weight: 400;
          color: var(--tc-text-body);
          line-height: 1.65;
          margin-bottom: 1rem;
        }

        /* Stack pills */
        .tc-stack {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
          margin-bottom: 1rem;
        }
        .tc-stack-pill {
          font-family: var(--font-body);
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.22rem 0.65rem;
          border-radius: 50px;
          background: var(--card-accent-bg, rgba(37,99,235,0.08));
          color: var(--card-accent, #2563eb);
          border: 1px solid var(--card-accent-border, rgba(37,99,235,0.2));
          letter-spacing: 0.03em;
        }

        /* Learn more link */
        .tc-card-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-family: var(--font-body);
          font-size: 0.82rem;
          font-weight: 600;
          color: var(--card-accent, #2563eb);
          text-decoration: none;
          opacity: 0;
          transform: translateX(-6px);
          transition: opacity 0.25s ease, transform 0.25s ease;
        }
        .tc-tech-card:hover .tc-card-link,
        .tc-tech-card.active .tc-card-link {
          opacity: 1;
          transform: translateX(0);
        }
        .tc-card-link svg { font-size: 0.7rem; transition: transform 0.2s; }
        .tc-card-link:hover svg { transform: translateX(3px); }

        /* ── Stats strip ── */
        .tc-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 0;
          border-top: 1px solid var(--tc-border);
          margin-top: 2.5rem;
        }
        .tc-stat {
          flex: 1 1 160px;
          padding: 1.5rem 1.25rem;
          text-align: center;
          border-right: 1px solid var(--tc-border);
        }
        .tc-stat:last-child { border-right: none; }
        .tc-stat-value {
          font-family: var(--font-display);
          font-size: 1.9rem;
          font-weight: 600;
          color: var(--tc-text-dark);
          line-height: 1;
          margin-bottom: 0.3rem;
        }
        .tc-stat-value span {
          background: linear-gradient(90deg, #2563eb, #7c3aed);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .tc-stat-label {
          font-family: var(--font-body);
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--tc-text-muted);
        }

        /* ── Responsive ── */
        @media (max-width: 900px) {
          .tc-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 600px) {
          .tc-grid       { grid-template-columns: 1fr; }
          .tc-card-wrap  { margin-top: -28px; }
          .tc-card-body  { padding: 1.5rem 1rem 1rem; }
          .tc-breadcrumb { padding: 0.75rem 1rem; }
          .tc-stat       { flex: 1 1 50%; }
          .tc-stat:nth-child(2) { border-right: none; }
        }
        @media (max-width: 400px) {
          .tc-hero-title { font-size: 1.75rem; }
        }
      `}</style>

      <div className="tc-page">

        {/* ── Hero Banner ── */}
        <div
          className="tc-hero"
          style={{ backgroundImage: `url(${Services3})` }}
          aria-label="Our Technology Stack"
        >
          <div className="tc-hero-overlay" aria-hidden="true" />
          <div className="tc-hero-content">
            <span className="tc-hero-eyebrow">Our Tech Stack</span>
            <h1 className="tc-hero-title">
              Cutting-Edge <span>Technology</span>
            </h1>
            <p className="tc-hero-sub">
              We merge innovation with proven technologies to build scalable,
              secure, and intelligent solutions across industries.
            </p>
          </div>
        </div>

        {/* ── Itservices component ── */}
        <Itservices />

        {/* ── Main content ── */}
        <div className="tc-container">
          <div className="tc-card-wrap">

            {/* Breadcrumb */}
            <nav className="tc-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <FaChevronRight aria-hidden="true" />
              <span>Technology</span>
            </nav>

            {/* Card body */}
            <div className="tc-card-body">

              {/* Section header */}
              <div className="tc-section-eyebrow">What We Build With</div>
              <h2 className="tc-section-title">Technology Expertise</h2>
              <div className="tc-underline" />
              <p className="tc-section-desc">
                Our strength lies in merging innovation with reliable technologies
                to build scalable, secure, and intelligent solutions that power{" "}
                <strong>automation, digital transformation, and AI-led growth</strong>{" "}
                across industries.
              </p>

              {/* Tech grid */}
              <div className="tc-grid">
                {TECH_ITEMS.map((tech) => (
                  <div
                    key={tech.id}
                    className={`tc-tech-card${activeId === tech.id ? " active" : ""}`}
                    style={{
                      "--card-accent":        tech.accent,
                      "--card-accent-bg":     tech.accentBg,
                      "--card-accent-border": tech.accentBorder,
                    }}
                    onClick={() => toggle(tech.id)}
                    role="button"
                    aria-pressed={activeId === tech.id}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && toggle(tech.id)}
                  >
                    <div className="tc-card-icon" aria-hidden="true">{tech.icon}</div>
                    <div className="tc-card-tag">{tech.tag}</div>
                    <div className="tc-card-title">{tech.title}</div>
                    <p className="tc-card-desc">{tech.desc}</p>

                    <div className="tc-stack">
                      {tech.stack.map((s) => (
                        <span key={s} className="tc-stack-pill">{s}</span>
                      ))}
                    </div>

                    <Link to="/service" className="tc-card-link">
                      Explore Services <FaArrowRight aria-hidden="true" />
                    </Link>
                  </div>
                ))}
              </div>

              {/* Stats strip */}
              <div className="tc-stats">
                {STATS.map(({ id, value, label }) => (
                  <div key={id} className="tc-stat">
                    <div className="tc-stat-value"><span>{value}</span></div>
                    <div className="tc-stat-label">{label}</div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>

        <WhatsApp />
      </div>
    </>
  );
}

export default Technology;